import { logger } from '../utils/logger';
import { Organization, OrgMember, OrgRole, PlanTier } from '@/types/index';
import api from '@/services/api/mobileApiService';

// ─── State ────────────────────────────────────────────────────────────────────

export interface OrgState {
  org: Organization | null;
  members: OrgMember[];
  orgRole: OrgRole | null;   // Current user's role in the org
  planTier: PlanTier;        // 'free' by default for solo users
  inviteLink: string | null; // Last generated invite link
  // Diagnostic: last fetchOrgData outcome — used to surface "why does
  // the UI say solo?" without devtools.
  orgFetchStatus: 'never' | 'ok' | 'null' | 'error';
  orgFetchError: string | null;
}

export const orgSlice: OrgState = {
  org: null,
  members: [],
  orgRole: null,
  planTier: 'free',
  inviteLink: null,
  orgFetchStatus: 'never',
  orgFetchError: null,
};

// ─── Server response shapes ─────────────────────────────────────────────────
//
// mobileApiService.{get,post,put,patch,delete}() return the raw HTTP response
// BODY (axios response.data). The org/project routes wrap their payload in a
// `{ success, data }` envelope, so the handlers below read `.data` off the
// returned body. Typing that envelope here keeps the de-wrap explicit instead
// of relying on `any`.

interface Envelope<T> {
  success?: boolean;
  data: T;
}

interface InviteEnvelope {
  invite_link?: string;
}

interface AcceptInviteEnvelope {
  org_id?: number;
}

// ─── Internal helpers ───────────────────────────────────────────────────────

/**
 * Extract a short, log/UI-safe summary from an unknown thrown value.
 * Catch clauses are typed `unknown` under strict mode; this narrows once
 * so the action bodies stay readable. Never surfaces stack traces.
 */
function errorMessage(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'message' in error) {
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return undefined;
}

/** Pull `error.response.status` (axios) without assuming the shape. */
function httpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: unknown }).response;
    if (response && typeof response === 'object' && 'status' in response) {
      const status = (response as { status?: unknown }).status;
      if (typeof status === 'number') return status;
    }
  }
  return undefined;
}

// ─── Actions ──────────────────────────────────────────────────────────────────

// NOTE: the `set`/`get` parameter types below intentionally mirror the loose
// store-composition signature used across every slice in this codebase. They
// are the seam where per-slice actions are merged into the full store; the
// strongly-typed surface lives in src/types/store.ts. Do not narrow these — it
// would diverge from the other slices and break store composition.
export type OrgActions = (
  set: (partial: ((state: any) => void) | Record<string, any>) => void,
  get: () => any
) => {
  setOrg: (org: Organization | null) => void;
  setOrgRole: (role: OrgRole | null) => void;
  setPlanTier: (tier: PlanTier) => void;
  fetchOrgData: () => Promise<void>;
  fetchOrgMembers: () => Promise<void>;
  createOrg: (name: string, slug: string) => Promise<Organization>;
  inviteMember: (email: string) => Promise<string>;
  removeMember: (userId: number) => Promise<void>;
  updateMemberRole: (userId: number, role: 'admin' | 'member') => Promise<void>;
  acceptInvite: (token: string) => Promise<void>;
  updateOrgTier: (tier: PlanTier) => Promise<void>;
  updateOrgSeats: (seats: number) => Promise<void>;
  claimProjectForOrg: (projectId: number) => Promise<void>;
  uploadOrgLogo: (dataUri: string) => Promise<void>;
  removeOrgLogo: () => Promise<void>;
  clearOrg: () => void;
  isOrgOwner: () => boolean;
  isOrgAdmin: () => boolean;
  canManageTeam: () => boolean;
  isEnterprise: () => boolean;
  isPro: () => boolean;
  isPaidTier: () => boolean;
  fetchProjectMembers: (projectId: number) => Promise<OrgMember[]>;
  updateProjectMembers: (projectId: number, userIds: number[]) => Promise<OrgMember[]>;
};

export const orgActions: OrgActions = (set, get) => ({
  setOrg: (org: Organization | null) => {
    set({ org });
  },

  setOrgRole: (role: OrgRole | null) => {
    set({ orgRole: role });
  },

  setPlanTier: (tier: PlanTier) => {
    set({ planTier: tier });
  },

  fetchOrgData: async (): Promise<void> => {
    try {
      // The server returns { success: true, data: <org-or-null> }. api.get()
      // returns that body, so we read `.data` to get the org-or-null.
      const response = await api.get<Envelope<Organization | null>>('/api/orgs/mine');
      const data = response.data;
      // `data === null` explicitly means "user is solo" — clear state.
      // `data` present means "user is in an org" — set state.
      // Any other shape (network fail, 500, malformed) is treated as a
      // transient error and we PRESERVE the previous state so a blip
      // doesn't flip the UI into the "create organization" view while the
      // user is actually in one.
      if (data) {
        set({
          org: data,
          orgRole: data.my_role ?? null,
          planTier: data.plan_tier ?? 'free',
          orgFetchStatus: 'ok',
          orgFetchError: null,
        });
        logger.debug('Org data loaded', { orgId: data.id, role: data.my_role });
      } else if (data === null) {
        set({
          org: null,
          orgRole: null,
          planTier: 'free',
          orgFetchStatus: 'null',
          orgFetchError: null,
        });
      } else {
        logger.warn('fetchOrgData: unexpected response shape — keeping previous state', { response: response.data });
        set({ orgFetchStatus: 'error', orgFetchError: 'Unexpected response shape' });
      }
    } catch (error: unknown) {
      const message = errorMessage(error);
      logger.warn('fetchOrgData: request failed — keeping previous state', { error: message });
      const status = httpStatus(error);
      const summary = status ? `HTTP ${status}` : (message || 'network error');
      set({ orgFetchStatus: 'error', orgFetchError: summary });
      // Intentionally do NOT clear state. Previous value (if any) wins.
    }
  },

  fetchOrgMembers: async (): Promise<void> => {
    const state = get();
    if (!state.org) return;
    try {
      const response = await api.get<Envelope<OrgMember[] | null>>(`/api/orgs/${state.org.id}/members`);
      set({ members: response.data ?? [] });
    } catch (error: unknown) {
      logger.error('fetchOrgMembers failed', { error: errorMessage(error) });
    }
  },

  createOrg: async (name: string, slug: string): Promise<Organization> => {
    const response = await api.post<Envelope<Organization | null>>('/api/orgs', { name, slug });
    const org = response.data;
    if (!org) throw new Error('Failed to create organization');
    set({ org, orgRole: 'owner', planTier: org.plan_tier });
    logger.info('Organization created', { orgId: org.id, name: org.name });
    return org;
  },

  inviteMember: async (email: string): Promise<string> => {
    const state = get();
    if (!state.org) throw new Error('No organization');
    const response = await api.post<Envelope<InviteEnvelope | null>>(
      `/api/orgs/${state.org.id}/invite`,
      { email }
    );
    const link: string = response.data?.invite_link ?? '';
    set({ inviteLink: link });
    logger.info('Invite link generated', { orgId: state.org.id, email });
    return link;
  },

  removeMember: async (userId: number): Promise<void> => {
    const state = get();
    if (!state.org) throw new Error('No organization');
    await api.delete(`/api/orgs/${state.org.id}/members/${userId}`);
    set((s: OrgState) => ({
      members: s.members.filter((m: OrgMember) => m.user_id !== userId),
    }));
    logger.info('Member removed', { orgId: state.org.id, userId });
  },

  updateMemberRole: async (userId: number, role: 'admin' | 'member'): Promise<void> => {
    const state = get();
    if (!state.org) throw new Error('No organization');
    await api.patch(`/api/orgs/${state.org.id}/members/${userId}`, { role });
    // Optimistically mutate in place to keep the list stable. If this
    // ever races with a member list refetch, the refetch wins.
    set((s: OrgState) => ({
      members: s.members.map((m: OrgMember) =>
        m.user_id === userId ? { ...m, org_role: role } : m
      ),
    }));
    logger.info('Member role updated', { orgId: state.org.id, userId, role });
  },

  acceptInvite: async (token: string): Promise<void> => {
    const response = await api.post<Envelope<AcceptInviteEnvelope | null>>(
      `/api/orgs/accept-invite/${token}`,
      {}
    );
    const data = response.data;
    if (data) {
      // Refresh org data after accepting
      await get().fetchOrgData();
      logger.info('Invite accepted', { orgId: data.org_id });
    }
  },

  updateOrgTier: async (tier: PlanTier): Promise<void> => {
    const state = get();
    if (!state.org) throw new Error('No organization');
    const response = await api.put<Envelope<Organization | null>>(
      `/api/orgs/${state.org.id}`,
      { plan_tier: tier }
    );
    const updated = response.data;
    if (updated) {
      set({ org: { ...state.org, plan_tier: updated.plan_tier }, planTier: updated.plan_tier });
    }
    logger.info('Org tier updated', { orgId: state.org.id, tier });
  },

  updateOrgSeats: async (seats: number): Promise<void> => {
    const state = get();
    if (!state.org) throw new Error('No organization');
    const response = await api.put<Envelope<Organization | null>>(
      `/api/orgs/${state.org.id}`,
      { seats_purchased: seats }
    );
    const updated = response.data;
    if (updated) {
      set({ org: { ...state.org, seats_purchased: updated.seats_purchased } });
    }
    logger.info('Org seats updated', { orgId: state.org.id, seats });
  },

  claimProjectForOrg: async (projectId: number): Promise<void> => {
    const state = get();
    if (!state.org) throw new Error('No organization');
    await api.patch(`/api/projects/${projectId}/org`, {});
    // Refresh the project list so the moved project shows its new
    // org_id and becomes shareable. fetchProjects lives on the project
    // slice; guard in case it's unavailable.
    try {
      const fetchProjects = get().fetchProjects as (() => Promise<void>) | undefined;
      if (fetchProjects) await fetchProjects();
    } catch { /* non-fatal */ }
    logger.info('Project claimed for org', { orgId: state.org.id, projectId });
  },

  uploadOrgLogo: async (dataUri: string): Promise<void> => {
    const state = get();
    if (!state.org) throw new Error('No organization');
    await api.put(`/api/orgs/${state.org.id}/logo`, { company_logo: dataUri });
    set({ org: { ...state.org, company_logo: dataUri } });
    logger.info('Org logo uploaded', { orgId: state.org.id });
  },

  removeOrgLogo: async (): Promise<void> => {
    const state = get();
    if (!state.org) throw new Error('No organization');
    await api.delete(`/api/orgs/${state.org.id}/logo`);
    set({ org: { ...state.org, company_logo: null } });
    logger.info('Org logo removed', { orgId: state.org.id });
  },

  clearOrg: () => {
    set({ org: null, members: [], orgRole: null, planTier: 'free', inviteLink: null });
  },

  isOrgOwner: (): boolean => {
    return get().orgRole === 'owner';
  },

  isOrgAdmin: (): boolean => {
    return get().orgRole === 'admin';
  },

  // True if the user can invite, remove, assign projects, or otherwise
  // manage the team. Owners AND admins both qualify. Billing actions
  // (plan change, company logo) stay owner-only and should gate on
  // isOrgOwner() instead.
  canManageTeam: (): boolean => {
    const role = get().orgRole;
    return role === 'owner' || role === 'admin';
  },

  isEnterprise: (): boolean => {
    return get().planTier === 'enterprise';
  },

  isPro: (): boolean => {
    return get().planTier === 'pro';
  },

  isPaidTier: (): boolean => {
    const tier = get().planTier;
    return tier === 'pro' || tier === 'enterprise';
  },

  fetchProjectMembers: async (projectId: number): Promise<OrgMember[]> => {
    try {
      const response = await api.get<Envelope<OrgMember[] | null>>(`/api/projects/${projectId}/members`);
      return response.data ?? [];
    } catch (error: unknown) {
      logger.error('fetchProjectMembers failed', { error: errorMessage(error), projectId });
      return [];
    }
  },

  updateProjectMembers: async (projectId: number, userIds: number[]): Promise<OrgMember[]> => {
    try {
      const response = await api.put<Envelope<OrgMember[] | null>>(
        `/api/projects/${projectId}/members`,
        { user_ids: userIds }
      );
      const members = response.data ?? [];
      logger.info('Project members updated', { projectId, count: members.length });
      return members;
    } catch (error: unknown) {
      logger.error('updateProjectMembers failed', { error: errorMessage(error), projectId });
      throw error;
    }
  },
});
